import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { QUERY_KEYS } from "app/data/common/queryKeys";

const RELATIVE_TIME_TICK_MS = 30_000;

export function useDockUsersRefresh(terminalNumber?: number) {
  const queryClient = useQueryClient();
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [, setRelativeTimeTick] = useState(0);

  useEffect(() => {
    if (lastRefreshedAt == null) return;
    const id = window.setInterval(() => setRelativeTimeTick((n) => n + 1), RELATIVE_TIME_TICK_MS);
    return () => clearInterval(id);
  }, [lastRefreshedAt]);

  const refreshAll = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const tasks: Promise<unknown>[] = [
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCK_EMPLOYEES] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCK_AUTH_EVENTS] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCK_SCAN_LOGS] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCK_EMPLOYEE_DETAIL] }),
      ];
      if (terminalNumber != null) {
        tasks.push(
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCK_OPERATIONS_SUMMARY, terminalNumber] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCK_TAGS, terminalNumber] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCK_ACTIVE_SESSIONS, terminalNumber] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCK_LAST_LOGINS, terminalNumber] })
        );
      }
      await Promise.all(tasks);
      setLastRefreshedAt(Date.now());
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient, terminalNumber]);

  const lastRefreshedLabel =
    lastRefreshedAt == null ? null : moment(lastRefreshedAt).fromNow();

  return {
    refreshAll,
    isRefreshing,
    lastRefreshedAt,
    lastRefreshedLabel,
    setLastRefreshedAt,
  };
}
